import type { Locale } from './locales';

// English (source of truth — defines the full key set).
export const en = {
    // Common
    'common.save': 'Save',
    'common.saving': 'Saving...',
    'common.saveChanges': 'Save Changes',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.refresh': 'Refresh',
    'common.create': 'Create',
    'common.creating': 'Creating...',
    'common.back': 'Back',
    'common.loading': 'Loading...',
    'common.validationFailed': 'Please fix the highlighted fields and try again.',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.enabled': 'Enabled',
    'common.disabled': 'Disabled',
    'common.published': 'Published',
    'common.draft': 'Draft',
    'common.paid': 'Paid',
    'common.unpaid': 'Unpaid',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.total': 'total',
    'common.status': 'Status',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.type': 'Type',
    'common.description': 'Description',
    'common.actions': 'Actions',
    'common.required': 'Required',
    'common.untitled': 'Untitled',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.page': 'Page',

    // Nav
    'nav.dashboard': 'Dashboard',
    'nav.users': 'Users',
    'nav.documents': 'Documents',
    'nav.templates': 'Templates',
    'nav.creditPackages': 'Credit Packages',
    'nav.creditSettings': 'Credit Settings',
    'nav.vouchers': 'Vouchers',
    'nav.paymentOrders': 'Payment Orders',
    'nav.jobQueue': 'Job Queue',
    'nav.aiUsage': 'AI Usage',
    'nav.documentUnlockSettings': 'Unlock Card',

    // Header / user menu
    'header.logout': 'Logout',
    'header.language': 'Language',

    // Login
    'login.title': 'Admin Panel',
    'login.description': 'Sign in with your administrator email',
    'login.emailLabel': 'Email',
    'login.emailPlaceholder': 'admin@example.com',
    'login.submit': 'Send magic link',
    'login.submitting': 'Sending...',
    'login.sent': 'A magic link has been sent to your email. Please check your inbox and click the link to sign in.',
    'login.sendAgain': 'Send again',
    'login.error': 'Failed to send magic link. Please try again.',
    'login.cooldown': 'Please wait before requesting another link.',
    'errorBoundary.title': 'Something went wrong',
    'errorBoundary.description': 'An unexpected error occurred. You can retry, or go back to the dashboard.',
    'errorBoundary.retry': 'Retry',
    'errorBoundary.home': 'Go to dashboard',

    // Access denied
    'accessDenied.title': 'Access Denied',
    'accessDenied.message': 'You don\'t have administrator privileges. Contact your administrator to request access.',
    'accessDenied.backToLogin': 'Back to login',

    // Magic link verification
    'magicLink.verifying': 'Verifying...',
    'magicLink.errorTitle': 'Authentication Error',
    'magicLink.errorUnexpected': 'Unexpected authentication response.',
    'magicLink.errorExpired': 'Failed to verify magic link. It may have expired.',
    'magicLink.backToLogin': 'Back to login',

    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.totalUsers': 'Total Users',
    'dashboard.documents': 'Documents',
    'dashboard.templates': 'Templates',
    'dashboard.activeJobs': 'Active Jobs',
    'dashboard.paymentOrders': 'Payment Orders',

    // Users
    'users.title': 'Users',
    'users.description': 'Manage user accounts and permissions',
    'users.search': 'Search by name or email...',
    'users.empty': 'No users found.',
    'users.loading': 'Loading users...',
    'users.loadError': 'Failed to load users.',

    // User detail
    'userDetail.information': 'User Information',
    'userDetail.accountEnabled': 'Account Enabled',
    'userDetail.billingData': 'Billing Data',
    'userDetail.noBillingData': 'No billing data provided.',
    'userDetail.systemPrivileges': 'System Privileges',
    'userDetail.privilegesHint': 'Changes are saved together with the user information above.',
    'userDetail.documents': 'Documents',
    'userDetail.documentsEmpty': 'No documents.',
    'userDetail.documentsLoading': 'Loading documents...',
    'userDetail.documentsLoadError': 'Failed to load documents.',
    'userDetail.markPaid': 'Mark Paid',
    'userDetail.markUnpaid': 'Mark Unpaid',
    'userDetail.invoice': 'Invoice',
    'userDetail.payment': 'Payment',
    'userDetail.saveSuccess': 'User updated successfully.',
    'userDetail.saveError': 'Failed to update user.',
    'userDetail.loading': 'Loading user...',
    'userDetail.loadError': 'Failed to load user.',
    'userDetail.billingStreet': 'Street',
    'userDetail.billingCity': 'City',
    'userDetail.billingResidency': 'Residency',
    'userDetail.markedPaid': 'Marked as paid.',
    'userDetail.markedUnpaid': 'Marked as unpaid.',
    'userDetail.paymentStatusError': 'Failed to update payment status.',
    'userDetail.invoiceError': 'Failed to get invoice URL.',

    // Documents list
    'documents.title': 'Documents',
    'documents.description': 'All documents from all users',
    'documents.empty': 'No documents found.',
    'documents.loadError': 'Failed to load documents.',
    'documents.user': 'User',
    'documents.created': 'Created',

    // Templates list
    'templates.title': 'Templates',
    'templates.description': 'Manage document templates',
    'templates.create': 'Create Template',
    'templates.duplicate': 'Duplicate template',
    'templates.empty': 'No templates found.',
    'templates.loading': 'Loading templates...',
    'templates.loadError': 'Failed to load templates.',

    // Template edit/create
    'templateEdit.loading': 'Loading template...',
    'templateEdit.loadError': 'Failed to load template.',
    'templateEdit.untitled': 'Untitled Template',
    'templateEdit.createTitle': 'Create Template',
    'templateEdit.saveSuccess': 'Template saved.',
    'templateEdit.saveError': 'Failed to save template.',
    'templateEdit.deleteSuccess': 'Template deleted.',
    'templateEdit.deleteError': 'Failed to delete template.',
    'templateEdit.createSuccess': 'Template created.',
    'templateEdit.createError': 'Failed to create template.',
    'templateEdit.nameRequired': 'Template name is required.',
    'templateEdit.deleteTitle': 'Delete Template',
    'templateEdit.deleteConfirm': 'Are you sure you want to delete this template? This action cannot be undone.',

    // Template form fields
    'templateForm.basicInfo': 'Basic Info',
    'templateForm.configuration': 'Configuration',
    'templateForm.documentTypes': 'Document Types',
    'templateForm.sections': 'Sections',
    'templateForm.labelRequired': 'Name *',
    'templateForm.publishedLabel': 'Published',
    'templateForm.iconPath': 'Icon Path',
    'templateForm.docxPath': 'DOCX Path',
    'templateForm.iconUpload': 'Icon',
    'templateForm.docxUpload': 'DOCX Template',
    'templateForm.language': 'Language',
    'templateForm.languageCz': 'Czech',
    'templateForm.languageSk': 'Slovak',
    'fileUpload.pick': 'Choose file',
    'fileUpload.uploading': 'Uploading...',
    'fileUpload.remove': 'Remove',
    'fileUpload.download': 'Download',
    'fileUpload.editPath': 'Edit path manually',
    'fileUpload.uploadError': 'Upload failed.',
    'fileUpload.uploadSuccess': 'File uploaded.',
    'fileUpload.noFile': 'No file selected',
    'templateForm.citationStyle': 'Citation Style',
    'templateForm.citationPrefix': 'Citation Page Number Prefix',
    'templateForm.citationCustomInstructions': 'Custom citation instructions (faculty-specific rules, examples)',
    'templateForm.captionGeneral': 'Caption Position (General)',
    'templateForm.captionImages': 'Caption Position (Images)',
    'templateForm.captionTables': 'Caption Position (Tables)',
    'templateForm.captionCharts': 'Caption Position (Charts)',
    'templateForm.captionChapterNumbers': 'Chapter-prefixed caption numbers (e.g. 1.1, 1.2, 2.1)',
    'templateForm.captionAbove': 'Above object',
    'templateForm.captionBelow': 'Below object',
    'templateForm.figurePrefixes': 'Figure Prefixes',
    'templateForm.figureImage': 'Image',
    'templateForm.figureTable': 'Table',
    'templateForm.figureChart': 'Chart',
    'templateForm.figureFormula': 'Formula',
    'templateForm.figureMap': 'Map',
    'templateForm.typeKey': 'new_type_key',
    'templateForm.addType': 'Add Type',
    'templateForm.typeLabel': 'Label',
    'templateForm.typeMinPages': 'Min Pages',
    'templateForm.typeMaxPages': 'Max Pages',
    'templateForm.typeAiPrompt': 'AI Prompt',
    'templateForm.variables': 'Variables',
    'templateForm.variableKey': 'variable_key',
    'templateForm.sectionId': 'ID',
    'templateForm.sectionTitle': 'Title',
    'templateForm.sectionType': 'Type',
    'templateForm.sectionInstructions': 'Instructions',
    'templateForm.sectionCountChars': 'Count Characters',
    'templateForm.sectionNoPageBreak': 'No page break after section',
    'templateForm.sectionOverview': 'Section overview — drag to reorder, click to jump',
    'templateForm.sectionTypeContent': 'Content',
    'templateForm.sectionTypeToC': 'Table of Contents',
    'templateForm.sectionTypeCitationList': 'Citation List',
    'templateForm.sectionTypeShortcutList': 'Shortcut List',
    'templateForm.sectionTypeFigureList': 'Figure List',
    'templateForm.sectionTypeAttachmentList': 'Attachment List',
    'templateForm.sectionTypeAttachments': 'Attachments',
    'templateForm.sectionTypeOtherAttachments': 'Other Attachments',
    'templateForm.addSection': 'Add Section',
    'templateForm.contentField': 'Content Field',
    'templateForm.hasContentArea': 'Has Content Area',
    'templateForm.contentId': 'Content ID',
    'templateForm.wordCount': 'Word Count',
    'templateForm.fields': 'Fields',
    'templateForm.fieldLabel': 'Label',
    'templateForm.fieldPlaceholder': 'Placeholder',
    'templateForm.fieldReq': 'Req',
    'templateForm.addField': 'Add Field',

    // Credit packages list
    'creditPackages.title': 'Credit Packages',
    'creditPackages.description': 'Manage credit packages available for purchase',
    'creditPackages.create': 'Create Package',
    'creditPackages.empty': 'No credit packages found.',
    'creditPackages.loading': 'Loading packages...',
    'creditPackages.loadError': 'Failed to load packages.',
    'creditPackages.priceCzk': 'Price CZK',
    'creditPackages.priceEur': 'Price EUR',
    'creditPackages.credits': 'Credits',

    // Credit package edit/create
    'creditPackageEdit.loading': 'Loading package...',
    'creditPackageEdit.loadError': 'Failed to load package.',
    'creditPackageEdit.untitled': 'Untitled Package',
    'creditPackageEdit.createTitle': 'Create Credit Package',
    'creditPackageEdit.saveSuccess': 'Package saved.',
    'creditPackageEdit.saveError': 'Failed to save package.',
    'creditPackageEdit.deleteSuccess': 'Package deleted.',
    'creditPackageEdit.deleteError': 'Failed to delete package.',
    'creditPackageEdit.createSuccess': 'Credit package created.',
    'creditPackageEdit.createError': 'Failed to create credit package.',
    'creditPackageEdit.nameRequired': 'Package name is required.',
    'creditPackageEdit.deleteTitle': 'Delete Credit Package',
    'creditPackageEdit.deleteConfirm': 'Are you sure you want to delete this package? This action cannot be undone.',

    // Credit package form
    'creditPackageForm.details': 'Package Details',
    'creditPackageForm.currency': 'Currency',
    'creditPackageForm.saleBadge': 'Sale badge',
    'creditPackageForm.saleBadgePlaceholder': 'e.g. SALE, -20%',
    'creditPackageForm.saleBadgeHint': 'Optional. Shown as a badge on the package card. Leave empty to hide.',

    // Credit settings
    'creditSettings.title': 'Credit Settings',
    'creditSettings.description': 'Configure credit costs for AI features',
    'creditSettings.saveSuccess': 'Credit settings saved.',
    'creditSettings.saveError': 'Failed to save credit settings.',
    'creditSettings.empty': 'No AI features found.',
    'creditSettings.loading': 'Loading settings...',
    'creditSettings.loadError': 'Failed to load credit settings.',
    'creditSettings.costPerUnit': 'Cost per Unit',
    'creditSettings.unit': 'Unit',
    'creditSettings.unitPlaceholder': 'e.g. 80 words, 1 page',

    // Vouchers list
    'vouchers.title': 'Vouchers',
    'vouchers.description': 'Manage discount vouchers',
    'vouchers.create': 'Create Voucher',
    'vouchers.empty': 'No vouchers found.',
    'vouchers.loading': 'Loading vouchers...',
    'vouchers.loadError': 'Failed to load vouchers.',
    'vouchers.code': 'Code',
    'vouchers.typePercentage': 'Percentage',
    'vouchers.typeFixed': 'Fixed Amount',
    'vouchers.value': 'Value',

    // Voucher edit/create
    'voucherEdit.loading': 'Loading voucher...',
    'voucherEdit.loadError': 'Failed to load voucher.',
    'voucherEdit.untitled': 'Untitled Voucher',
    'voucherEdit.createTitle': 'Create Voucher',
    'voucherEdit.saveSuccess': 'Voucher saved.',
    'voucherEdit.saveError': 'Failed to save voucher.',
    'voucherEdit.deleteSuccess': 'Voucher deleted.',
    'voucherEdit.deleteError': 'Failed to delete voucher.',
    'voucherEdit.createSuccess': 'Voucher created.',
    'voucherEdit.createError': 'Failed to create voucher.',
    'voucherEdit.codeRequired': 'Voucher code is required.',
    'voucherEdit.deleteTitle': 'Delete Voucher',
    'voucherEdit.deleteConfirm': 'Are you sure you want to delete this voucher? This action cannot be undone.',

    // Voucher form
    'voucherForm.details': 'Voucher Details',
    'voucherForm.codeRequired': 'Code *',
    'voucherForm.minPurchase': 'Min Purchase Amount',
    'voucherForm.minPurchasePlaceholder': 'No minimum',
    'voucherForm.maxUsesPerUser': 'Max Uses Per User',
    'voucherForm.maxUsesPerUserPlaceholder': 'Unlimited',
    'voucherForm.expiresAt': 'Expires At',

    // Job queue list
    'jobQueue.title': 'Job Queue',
    'jobQueue.description': 'Monitor background jobs and tasks',
    'jobQueue.empty': 'No jobs found.',
    'jobQueue.loading': 'Loading jobs...',
    'jobQueue.loadError': 'Failed to load jobs.',
    'jobQueue.jobType': 'Job Type',
    'jobQueue.status': 'Status',
    'jobQueue.document': 'Document',
    'jobQueue.created': 'Created',
    'jobQueue.status.Pending': 'Pending',
    'jobQueue.status.InProgress': 'In Progress',
    'jobQueue.status.Completed': 'Completed',
    'jobQueue.status.Failed': 'Failed',
    'jobQueue.status.CancelRequested': 'Cancel Requested',
    'jobQueue.status.Cancelled': 'Cancelled',
    'jobQueue.type.AiProofread': 'AI Proofread',

    // Job queue detail
    'jobQueueDetail.title': 'Job Detail',
    'jobQueueDetail.loading': 'Loading job...',
    'jobQueueDetail.loadError': 'Failed to load job.',
    'jobQueueDetail.information': 'Job Information',
    'jobQueueDetail.timestamps': 'Timestamps',
    'jobQueueDetail.document': 'Document',
    'jobQueueDetail.error': 'Error',
    'jobQueueDetail.params': 'Parameters',
    'jobQueueDetail.progress': 'Progress',
    'jobQueueDetail.log': 'Log',
    'jobQueueDetail.noLog': 'No log entries.',
    'jobQueueDetail.retryCount': 'Retry Count',
    'jobQueueDetail.maxTokens': 'Max Tokens',
    'jobQueueDetail.startedAt': 'Started',
    'jobQueueDetail.completedAt': 'Completed',
    'jobQueueDetail.lockedAt': 'Locked',
    'jobQueueDetail.cancel': 'Cancel Job',
    'jobQueueDetail.cancelConfirmTitle': 'Cancel Job',
    'jobQueueDetail.cancelConfirmBody': 'Request cancellation of this job? The worker will stop at the next safe point.',
    'jobQueueDetail.cancelSuccess': 'Cancel requested.',
    'jobQueueDetail.cancelError': 'Failed to cancel job.',

    // AI Usage
    'aiUsage.title': 'AI Usage',
    'aiUsage.description': 'Overview of AI feature usage and estimated costs',
    'aiUsage.loadError': 'Failed to load usage data',
    'aiUsage.totalRequests': 'Total Requests',
    'aiUsage.inputTokens': 'Input Tokens',
    'aiUsage.outputTokens': 'Output Tokens',
    'aiUsage.estimatedCost': 'Estimated Cost',
    'aiUsage.byActivity': 'By Activity',
    'aiUsage.recentLogs': 'Recent Logs',
    'aiUsage.colDate': 'Date',
    'aiUsage.colUser': 'User',
    'aiUsage.colDocument': 'Document',
    'aiUsage.colActivity': 'Activity',
    'aiUsage.colModel': 'Model',
    'aiUsage.colTokensIn': 'Tokens In',
    'aiUsage.colTokensOut': 'Tokens Out',
    'aiUsage.colCost': 'Est. Cost',
    'aiUsage.colCount': 'Requests',
    'aiUsage.empty': 'No usage data',
    'aiUsage.priceNote': '* Prices are estimates based on publicly available model pricing.',
    'aiUsage.showing': 'Showing last',
    'aiUsage.records': 'records',
    'aiUsage.activity.generateCitation': 'Citation generation',
    'aiUsage.activity.inlineCompletion': 'Text completion',
    'aiUsage.activity.aiProofreadingWithTools': 'AI proofreading',

    // Document Unlock Settings
    'documentUnlockSettings.title': 'Document Unlock Card',
    'documentUnlockSettings.description': 'Configure the pricing card shown to users when unlocking a document.',
    'documentUnlockSettings.cardTitle': 'Card title',
    'documentUnlockSettings.cardDescription': 'Card description',
    'documentUnlockSettings.priceCZK': 'Price CZK',
    'documentUnlockSettings.priceEUR': 'Price EUR',
    'documentUnlockSettings.benefits': 'Benefits (checkboxes)',
    'documentUnlockSettings.benefitPlaceholder': 'Add new benefit...',
    'documentUnlockSettings.loadError': 'Failed to load settings',
    'documentUnlockSettings.saveSuccess': 'Settings saved',
    'documentUnlockSettings.saveError': 'Failed to save settings',

    // Dialogs
    'dialog.unsavedChanges': 'Unsaved Changes',
    'dialog.unsavedMessage': 'You have unsaved changes. Refreshing will discard them. Continue?',
    'dialog.discardAndRefresh': 'Discard & Refresh',
    'dialog.deleteTitle': 'Delete',
    'dialog.deleteConfirm': 'Are you sure you want to delete this? This action cannot be undone.',
} as const;

export type TranslationKey = keyof typeof en;

// Czech — must have the same keys. TS checks this via the Record type.
export const cs: Record<TranslationKey, string> = {
    // Common
    'common.save': 'Uložit',
    'common.saving': 'Ukládání...',
    'common.saveChanges': 'Uložit změny',
    'common.cancel': 'Zrušit',
    'common.delete': 'Smazat',
    'common.edit': 'Upravit',
    'common.refresh': 'Obnovit',
    'common.create': 'Vytvořit',
    'common.creating': 'Vytváření...',
    'common.back': 'Zpět',
    'common.loading': 'Načítání...',
    'common.validationFailed': 'Opravte zvýrazněná pole a zkuste to znovu.',
    'common.active': 'Aktivní',
    'common.inactive': 'Neaktivní',
    'common.enabled': 'Povoleno',
    'common.disabled': 'Zakázáno',
    'common.published': 'Publikováno',
    'common.draft': 'Koncept',
    'common.paid': 'Zaplaceno',
    'common.unpaid': 'Nezaplaceno',
    'common.yes': 'Ano',
    'common.no': 'Ne',
    'common.total': 'celkem',
    'common.status': 'Stav',
    'common.name': 'Jméno',
    'common.email': 'E-mail',
    'common.type': 'Typ',
    'common.description': 'Popis',
    'common.actions': 'Akce',
    'common.required': 'Povinné',
    'common.untitled': 'Bez názvu',
    'common.previous': 'Předchozí',
    'common.next': 'Další',
    'common.page': 'Strana',

    // Nav
    'nav.dashboard': 'Přehled',
    'nav.users': 'Uživatelé',
    'nav.documents': 'Dokumenty',
    'nav.templates': 'Šablony',
    'nav.creditPackages': 'Kreditní balíčky',
    'nav.creditSettings': 'Nastavení kreditů',
    'nav.vouchers': 'Vouchery',
    'nav.paymentOrders': 'Platební objednávky',
    'nav.jobQueue': 'Fronta úloh',
    'nav.aiUsage': 'AI Využití',
    'nav.documentUnlockSettings': 'Karta odemčení',

    // Header / user menu
    'header.logout': 'Odhlásit',
    'header.language': 'Jazyk',

    // Login
    'login.title': 'Administrace',
    'login.description': 'Přihlaste se administrátorským e-mailem',
    'login.emailLabel': 'E-mail',
    'login.emailPlaceholder': 'admin@example.com',
    'login.submit': 'Odeslat odkaz pro přihlášení',
    'login.submitting': 'Odesílání...',
    'login.sent': 'Odkaz pro přihlášení byl odeslán na váš e-mail. Zkontrolujte si schránku a klikněte na odkaz pro přihlášení.',
    'login.sendAgain': 'Odeslat znovu',
    'login.error': 'Odeslání odkazu se nezdařilo. Zkuste to prosím znovu.',
    'login.cooldown': 'Před další žádostí o odkaz chvíli počkejte.',
    'errorBoundary.title': 'Něco se pokazilo',
    'errorBoundary.description': 'Došlo k neočekávané chybě. Můžete zkusit znovu nebo se vrátit na dashboard.',
    'errorBoundary.retry': 'Zkusit znovu',
    'errorBoundary.home': 'Přejít na dashboard',

    // Access denied
    'accessDenied.title': 'Přístup odepřen',
    'accessDenied.message': 'Nemáte administrátorská oprávnění. Obraťte se prosím na svého administrátora.',
    'accessDenied.backToLogin': 'Zpět na přihlášení',

    // Magic link verification
    'magicLink.verifying': 'Ověřování...',
    'magicLink.errorTitle': 'Chyba ověření',
    'magicLink.errorUnexpected': 'Neočekávaná odpověď ověření.',
    'magicLink.errorExpired': 'Ověření odkazu se nezdařilo. Možná vypršel.',
    'magicLink.backToLogin': 'Zpět na přihlášení',

    // Dashboard
    'dashboard.title': 'Přehled',
    'dashboard.welcome': 'Vítejte zpět',
    'dashboard.totalUsers': 'Celkem uživatelů',
    'dashboard.documents': 'Dokumenty',
    'dashboard.templates': 'Šablony',
    'dashboard.activeJobs': 'Aktivní úlohy',
    'dashboard.paymentOrders': 'Platební objednávky',

    // Users
    'users.title': 'Uživatelé',
    'users.description': 'Spravujte uživatelské účty a oprávnění',
    'users.search': 'Hledat podle jména nebo e-mailu...',
    'users.empty': 'Žádní uživatelé nenalezeni.',
    'users.loading': 'Načítání uživatelů...',
    'users.loadError': 'Načítání uživatelů se nezdařilo.',

    // User detail
    'userDetail.information': 'Informace o uživateli',
    'userDetail.accountEnabled': 'Účet povolen',
    'userDetail.billingData': 'Fakturační údaje',
    'userDetail.noBillingData': 'Žádné fakturační údaje.',
    'userDetail.systemPrivileges': 'Systémová oprávnění',
    'userDetail.privilegesHint': 'Změny se ukládají spolu s informacemi o uživateli výše.',
    'userDetail.documents': 'Dokumenty',
    'userDetail.documentsEmpty': 'Žádné dokumenty.',
    'userDetail.documentsLoading': 'Načítání dokumentů...',
    'userDetail.documentsLoadError': 'Načítání dokumentů se nezdařilo.',
    'userDetail.markPaid': 'Označit zaplaceno',
    'userDetail.markUnpaid': 'Označit nezaplaceno',
    'userDetail.invoice': 'Faktura',
    'userDetail.payment': 'Platba',
    'userDetail.saveSuccess': 'Uživatel úspěšně upraven.',
    'userDetail.saveError': 'Úprava uživatele se nezdařila.',
    'userDetail.loading': 'Načítání uživatele...',
    'userDetail.loadError': 'Načítání uživatele se nezdařilo.',
    'userDetail.billingStreet': 'Ulice',
    'userDetail.billingCity': 'Město',
    'userDetail.billingResidency': 'Rezidence',
    'userDetail.markedPaid': 'Označeno jako zaplacené.',
    'userDetail.markedUnpaid': 'Označeno jako nezaplacené.',
    'userDetail.paymentStatusError': 'Aktualizace stavu platby se nezdařila.',
    'userDetail.invoiceError': 'Získání URL faktury se nezdařilo.',

    // Documents list
    'documents.title': 'Dokumenty',
    'documents.description': 'Všechny dokumenty od všech uživatelů',
    'documents.empty': 'Žádné dokumenty nenalezeny.',
    'documents.loadError': 'Načítání dokumentů se nezdařilo.',
    'documents.user': 'Uživatel',
    'documents.created': 'Vytvořeno',

    // Templates list
    'templates.title': 'Šablony',
    'templates.description': 'Spravujte šablony dokumentů',
    'templates.create': 'Vytvořit šablonu',
    'templates.duplicate': 'Duplikovat šablonu',
    'templates.empty': 'Žádné šablony nenalezeny.',
    'templates.loading': 'Načítání šablon...',
    'templates.loadError': 'Načítání šablon se nezdařilo.',

    // Template edit/create
    'templateEdit.loading': 'Načítání šablony...',
    'templateEdit.loadError': 'Načítání šablony se nezdařilo.',
    'templateEdit.untitled': 'Šablona bez názvu',
    'templateEdit.createTitle': 'Vytvořit šablonu',
    'templateEdit.saveSuccess': 'Šablona uložena.',
    'templateEdit.saveError': 'Uložení šablony se nezdařilo.',
    'templateEdit.deleteSuccess': 'Šablona smazána.',
    'templateEdit.deleteError': 'Smazání šablony se nezdařilo.',
    'templateEdit.createSuccess': 'Šablona vytvořena.',
    'templateEdit.createError': 'Vytvoření šablony se nezdařilo.',
    'templateEdit.nameRequired': 'Název šablony je povinný.',
    'templateEdit.deleteTitle': 'Smazat šablonu',
    'templateEdit.deleteConfirm': 'Opravdu chcete tuto šablonu smazat? Tato akce je nevratná.',

    // Template form fields
    'templateForm.basicInfo': 'Základní údaje',
    'templateForm.configuration': 'Konfigurace',
    'templateForm.documentTypes': 'Typy dokumentů',
    'templateForm.sections': 'Sekce',
    'templateForm.labelRequired': 'Název *',
    'templateForm.publishedLabel': 'Publikováno',
    'templateForm.iconPath': 'Cesta k ikoně',
    'templateForm.iconUpload': 'Ikona',
    'templateForm.docxUpload': 'DOCX šablona',
    'fileUpload.pick': 'Vybrat soubor',
    'fileUpload.uploading': 'Nahrávání...',
    'fileUpload.remove': 'Odstranit',
    'fileUpload.download': 'Stáhnout',
    'fileUpload.editPath': 'Upravit cestu ručně',
    'fileUpload.uploadError': 'Nahrání se nezdařilo.',
    'fileUpload.uploadSuccess': 'Soubor nahrán.',
    'fileUpload.noFile': 'Žádný soubor nevybrán',
    'templateForm.docxPath': 'Cesta k DOCX',
    'templateForm.language': 'Jazyk',
    'templateForm.languageCz': 'Čeština',
    'templateForm.languageSk': 'Slovenština',
    'templateForm.citationStyle': 'Styl citace',
    'templateForm.citationPrefix': 'Prefix čísla stránky v citaci',
    'templateForm.citationCustomInstructions': 'Vlastní pokyny pro citace (pravidla fakulty, příklady)',
    'templateForm.captionGeneral': 'Pozice popisku (obecně)',
    'templateForm.captionImages': 'Pozice popisku (obrázky)',
    'templateForm.captionTables': 'Pozice popisku (tabulky)',
    'templateForm.captionCharts': 'Pozice popisku (grafy)',
    'templateForm.captionChapterNumbers': 'Číslovanie popiskov s kapitolou (napr. 1.1, 1.2, 2.1)',
    'templateForm.captionAbove': 'Nad objektem',
    'templateForm.captionBelow': 'Pod objektem',
    'templateForm.figurePrefixes': 'Prefixy popisků',
    'templateForm.figureImage': 'Obrázek',
    'templateForm.figureTable': 'Tabulka',
    'templateForm.figureChart': 'Graf',
    'templateForm.figureFormula': 'Vzorec',
    'templateForm.figureMap': 'Mapa',
    'templateForm.typeKey': 'klic_noveho_typu',
    'templateForm.addType': 'Přidat typ',
    'templateForm.typeLabel': 'Název',
    'templateForm.typeMinPages': 'Min. stran',
    'templateForm.typeMaxPages': 'Max. stran',
    'templateForm.typeAiPrompt': 'AI prompt',
    'templateForm.variables': 'Proměnné',
    'templateForm.variableKey': 'klic_promenne',
    'templateForm.sectionId': 'ID',
    'templateForm.sectionTitle': 'Název',
    'templateForm.sectionType': 'Typ',
    'templateForm.sectionInstructions': 'Instrukce',
    'templateForm.sectionCountChars': 'Počítat znaky',
    'templateForm.sectionNoPageBreak': 'Bez odřádkování stránky za sekcí',
    'templateForm.sectionOverview': 'Přehled sekcí — přetáhnout pro přeřazení, klik pro přechod',
    'templateForm.sectionTypeContent': 'Obsah',
    'templateForm.sectionTypeToC': 'Obsah (TOC)',
    'templateForm.sectionTypeCitationList': 'Seznam citací',
    'templateForm.sectionTypeShortcutList': 'Seznam zkratek',
    'templateForm.sectionTypeFigureList': 'Seznam OGT',
    'templateForm.sectionTypeAttachmentList': 'Seznam příloh',
    'templateForm.sectionTypeAttachments': 'Přílohy',
    'templateForm.sectionTypeOtherAttachments': 'Ostatní přílohy',
    'templateForm.addSection': 'Přidat sekci',
    'templateForm.contentField': 'Pole obsahu',
    'templateForm.hasContentArea': 'Má obsahovou oblast',
    'templateForm.contentId': 'ID obsahu',
    'templateForm.wordCount': 'Počet slov',
    'templateForm.fields': 'Pole',
    'templateForm.fieldLabel': 'Název',
    'templateForm.fieldPlaceholder': 'Placeholder',
    'templateForm.fieldReq': 'Pov.',
    'templateForm.addField': 'Přidat pole',

    // Credit packages list
    'creditPackages.title': 'Kreditní balíčky',
    'creditPackages.description': 'Spravujte kreditní balíčky k prodeji',
    'creditPackages.create': 'Vytvořit balíček',
    'creditPackages.empty': 'Žádné kreditní balíčky nenalezeny.',
    'creditPackages.loading': 'Načítání balíčků...',
    'creditPackages.loadError': 'Načítání balíčků se nezdařilo.',
    'creditPackages.priceCzk': 'Cena CZK',
    'creditPackages.priceEur': 'Cena EUR',
    'creditPackages.credits': 'Kredity',

    // Credit package edit/create
    'creditPackageEdit.loading': 'Načítání balíčku...',
    'creditPackageEdit.loadError': 'Načítání balíčku se nezdařilo.',
    'creditPackageEdit.untitled': 'Balíček bez názvu',
    'creditPackageEdit.createTitle': 'Vytvořit kreditní balíček',
    'creditPackageEdit.saveSuccess': 'Balíček uložen.',
    'creditPackageEdit.saveError': 'Uložení balíčku se nezdařilo.',
    'creditPackageEdit.deleteSuccess': 'Balíček smazán.',
    'creditPackageEdit.deleteError': 'Smazání balíčku se nezdařilo.',
    'creditPackageEdit.createSuccess': 'Kreditní balíček vytvořen.',
    'creditPackageEdit.createError': 'Vytvoření balíčku se nezdařilo.',
    'creditPackageEdit.nameRequired': 'Název balíčku je povinný.',
    'creditPackageEdit.deleteTitle': 'Smazat kreditní balíček',
    'creditPackageEdit.deleteConfirm': 'Opravdu chcete tento balíček smazat? Tato akce je nevratná.',

    // Credit package form
    'creditPackageForm.details': 'Detaily balíčku',
    'creditPackageForm.currency': 'Měna',
    'creditPackageForm.saleBadge': 'Štítek akce',
    'creditPackageForm.saleBadgePlaceholder': 'např. AKCE, -20 %',
    'creditPackageForm.saleBadgeHint': 'Volitelné. Zobrazí se jako štítek na kartě balíčku. Ponechte prázdné pro skrytí.',

    // Credit settings
    'creditSettings.title': 'Nastavení kreditů',
    'creditSettings.description': 'Nastavte ceny kreditů za AI funkce',
    'creditSettings.saveSuccess': 'Nastavení kreditů uloženo.',
    'creditSettings.saveError': 'Uložení nastavení se nezdařilo.',
    'creditSettings.empty': 'Žádné AI funkce nenalezeny.',
    'creditSettings.loading': 'Načítání nastavení...',
    'creditSettings.loadError': 'Načítání nastavení se nezdařilo.',
    'creditSettings.costPerUnit': 'Cena za jednotku',
    'creditSettings.unit': 'Jednotka',
    'creditSettings.unitPlaceholder': 'např. 80 slov, 1 stránka',

    // Vouchers list
    'vouchers.title': 'Vouchery',
    'vouchers.description': 'Spravujte slevové vouchery',
    'vouchers.create': 'Vytvořit voucher',
    'vouchers.empty': 'Žádné vouchery nenalezeny.',
    'vouchers.loading': 'Načítání voucherů...',
    'vouchers.loadError': 'Načítání voucherů se nezdařilo.',
    'vouchers.code': 'Kód',
    'vouchers.typePercentage': 'Procento',
    'vouchers.typeFixed': 'Pevná částka',
    'vouchers.value': 'Hodnota',

    // Voucher edit/create
    'voucherEdit.loading': 'Načítání voucheru...',
    'voucherEdit.loadError': 'Načítání voucheru se nezdařilo.',
    'voucherEdit.untitled': 'Voucher bez názvu',
    'voucherEdit.createTitle': 'Vytvořit voucher',
    'voucherEdit.saveSuccess': 'Voucher uložen.',
    'voucherEdit.saveError': 'Uložení voucheru se nezdařilo.',
    'voucherEdit.deleteSuccess': 'Voucher smazán.',
    'voucherEdit.deleteError': 'Smazání voucheru se nezdařilo.',
    'voucherEdit.createSuccess': 'Voucher vytvořen.',
    'voucherEdit.createError': 'Vytvoření voucheru se nezdařilo.',
    'voucherEdit.codeRequired': 'Kód voucheru je povinný.',
    'voucherEdit.deleteTitle': 'Smazat voucher',
    'voucherEdit.deleteConfirm': 'Opravdu chcete tento voucher smazat? Tato akce je nevratná.',

    // Voucher form
    'voucherForm.details': 'Detaily voucheru',
    'voucherForm.codeRequired': 'Kód *',
    'voucherForm.minPurchase': 'Minimální částka nákupu',
    'voucherForm.minPurchasePlaceholder': 'Žádné minimum',
    'voucherForm.maxUsesPerUser': 'Max. použití na uživatele',
    'voucherForm.maxUsesPerUserPlaceholder': 'Neomezeně',
    'voucherForm.expiresAt': 'Vyprší dne',

    // Job queue list
    'jobQueue.title': 'Fronta úloh',
    'jobQueue.description': 'Sledujte fronty úloh na pozadí',
    'jobQueue.empty': 'Žádné úlohy nenalezeny.',
    'jobQueue.loading': 'Načítání úloh...',
    'jobQueue.loadError': 'Načítání úloh se nezdařilo.',
    'jobQueue.jobType': 'Typ úlohy',
    'jobQueue.status': 'Stav',
    'jobQueue.document': 'Dokument',
    'jobQueue.created': 'Vytvořeno',
    'jobQueue.status.Pending': 'Čeká',
    'jobQueue.status.InProgress': 'Probíhá',
    'jobQueue.status.Completed': 'Dokončeno',
    'jobQueue.status.Failed': 'Selhalo',
    'jobQueue.status.CancelRequested': 'Žádost o zrušení',
    'jobQueue.status.Cancelled': 'Zrušeno',
    'jobQueue.type.AiProofread': 'AI korektura',

    // Job queue detail
    'jobQueueDetail.title': 'Detail úlohy',
    'jobQueueDetail.loading': 'Načítání úlohy...',
    'jobQueueDetail.loadError': 'Načítání úlohy se nezdařilo.',
    'jobQueueDetail.information': 'Informace o úloze',
    'jobQueueDetail.timestamps': 'Časové značky',
    'jobQueueDetail.document': 'Dokument',
    'jobQueueDetail.error': 'Chyba',
    'jobQueueDetail.params': 'Parametry',
    'jobQueueDetail.progress': 'Průběh',
    'jobQueueDetail.log': 'Log',
    'jobQueueDetail.noLog': 'Žádné záznamy v logu.',
    'jobQueueDetail.retryCount': 'Počet pokusů',
    'jobQueueDetail.maxTokens': 'Maximum tokenů',
    'jobQueueDetail.startedAt': 'Zahájeno',
    'jobQueueDetail.completedAt': 'Dokončeno',
    'jobQueueDetail.lockedAt': 'Uzamčeno',
    'jobQueueDetail.cancel': 'Zrušit úlohu',
    'jobQueueDetail.cancelConfirmTitle': 'Zrušit úlohu',
    'jobQueueDetail.cancelConfirmBody': 'Opravdu chcete požádat o zrušení této úlohy? Worker ji zastaví v nejbližším bezpečném bodě.',
    'jobQueueDetail.cancelSuccess': 'Požadavek na zrušení odeslán.',
    'jobQueueDetail.cancelError': 'Zrušení úlohy se nezdařilo.',

    // AI Usage
    'aiUsage.title': 'AI Využití',
    'aiUsage.description': 'Přehled využití AI funkcí a odhadované náklady',
    'aiUsage.loadError': 'Nepodařilo se načíst data o využití',
    'aiUsage.totalRequests': 'Celkem požadavků',
    'aiUsage.inputTokens': 'Vstupní tokeny',
    'aiUsage.outputTokens': 'Výstupní tokeny',
    'aiUsage.estimatedCost': 'Odh. náklady',
    'aiUsage.byActivity': 'Podle aktivity',
    'aiUsage.recentLogs': 'Nedávné záznamy',
    'aiUsage.colDate': 'Datum',
    'aiUsage.colUser': 'Uživatel',
    'aiUsage.colDocument': 'Dokument',
    'aiUsage.colActivity': 'Aktivita',
    'aiUsage.colModel': 'Model',
    'aiUsage.colTokensIn': 'Tokeny vstup',
    'aiUsage.colTokensOut': 'Tokeny výstup',
    'aiUsage.colCost': 'Odh. cena',
    'aiUsage.colCount': 'Požadavků',
    'aiUsage.empty': 'Žádná data o využití',
    'aiUsage.priceNote': '* Ceny jsou orientační, kalkulace vychází z veřejně dostupných cen modelů.',
    'aiUsage.showing': 'Zobrazeno posledních',
    'aiUsage.records': 'záznamů',
    'aiUsage.activity.generateCitation': 'Generování citace',
    'aiUsage.activity.inlineCompletion': 'Doplnění textu',
    'aiUsage.activity.aiProofreadingWithTools': 'Korektura AI',

    // Document Unlock Settings
    'documentUnlockSettings.title': 'Karta odemčení dokumentu',
    'documentUnlockSettings.description': 'Nastavení cenové karty zobrazené uživatelům při odemčení dokumentu.',
    'documentUnlockSettings.cardTitle': 'Název karty',
    'documentUnlockSettings.cardDescription': 'Popis karty',
    'documentUnlockSettings.priceCZK': 'Cena CZK',
    'documentUnlockSettings.priceEUR': 'Cena EUR',
    'documentUnlockSettings.benefits': 'Výhody (zaškrtávací políčka)',
    'documentUnlockSettings.benefitPlaceholder': 'Přidat výhodu...',
    'documentUnlockSettings.loadError': 'Nepodařilo se načíst nastavení',
    'documentUnlockSettings.saveSuccess': 'Nastavení uloženo',
    'documentUnlockSettings.saveError': 'Nepodařilo se uložit nastavení',

    // Dialogs
    'dialog.unsavedChanges': 'Neuložené změny',
    'dialog.unsavedMessage': 'Máte neuložené změny. Obnovením je zahodíte. Pokračovat?',
    'dialog.discardAndRefresh': 'Zahodit a obnovit',
    'dialog.deleteTitle': 'Smazat',
    'dialog.deleteConfirm': 'Opravdu chcete tuto položku smazat? Tato akce je nevratná.',
};

export const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = {
    en,
    cs,
};
